import * as React from 'react'
import FlexView from '@/modules/shared/components/FlexView'
import moment from 'moment'
import { useLocation, useNavigate } from 'react-router-dom'

import { DriverCalendarDayModel } from 'app/models/calendar/driverCalendarDayModel'
import { DayType } from 'app/constants/dayType'
import { usePermissionChecker } from '@/modules/shared/hooks/usePermissionChecker'
import {
  faCarSolid,
  FaIcon,
  faMapPinSolid,
  faTrashSolid,
  faUserSolid,
} from '@/lib/fa'
import useCalendarService from '@/modules/drivers/services/CalendarService'
import { ConfirmModal } from '@/modules/drivers/modals/ConfirmModal'
import AssignActivitiesToDriverModal from '@/modules/vehicles/modals/calendar/AssignActivitiesToDriverModal'
import AssignToSelectedDriverModal from '@/modules/vehicles/modals/calendar/AssignToSelectedDriverModal'
import useVehicleActivityService from '@/modules/vehicles/services/ActivityService'
import { notifyOperationResults } from '@/modules/shared/lib/operationResults'

import { MenuItem, MenuDivider, ControlledMenu, SubMenu } from '@szhsin/react-menu'
import '@szhsin/react-menu/dist/index.css'
import useFormatDate from '@/modules/drivers/hooks/useFormatDate'
import { CalendarMenuAction } from './VehicleCalendarDayItem'
import { useDayEvents } from '@/modules/drivers/hooks/useDayEvents'
type DayContextMenuProps = {
  companyId: string
  day: DriverCalendarDayModel
  vehicleId: number
  translate: (text) => any
  onCalendarAction: (
    day: DriverCalendarDayModel,
    action: CalendarMenuAction,
    data?: any,
  ) => void
  refreshCalendar?: () => Promise<void>
  anchorPoint?: { x: number; y: number }
  isOpen?: boolean
  onClose?: () => void
  selectedDays?: Array<DriverCalendarDayModel>
  currentSelectedDay?: DriverCalendarDayModel | null
  setSelectedDay?: (day: DriverCalendarDayModel | null) => void
}

export const VehicleDayContextMenu: React.FC<DayContextMenuProps> = (props) => {
  const {
    day,
    companyId,
    vehicleId,
    translate,
    refreshCalendar,
    anchorPoint,
    isOpen = false,
    onClose,
    selectedDays = [],
  } = props
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { changeVehicleDayTypes } = useCalendarService()
  const {
    assignBorderCrossingsToSelectedDriver,
    assignTmsDataToSelectedDriver,
  } = useVehicleActivityService()
  const dayEvents = useDayEvents()

  const [isConfirmModalOpen, setIsConfirmModalOpen] = React.useState(false)
  const [isAssignDriverModalOpen, setIsAssignDriverModalOpen] =
    React.useState(false)
  const [assignToDriverModal, setAssignToDriverModal] = React.useState<
    'border' | 'tms' | null
  >(null)
  const [selectedDayType, setSelectedDayType] = React.useState<DayType | null>(
    null,
  )
  const [isLoading, setIsLoading] = React.useState(false)
  const [confirmAction, setConfirmAction] = React.useState<{
    title: string
    text: string
    action: () => Promise<void>
    datesList?: string[]
  } | null>(null)

  const can = usePermissionChecker(companyId)

  const formatDateToString = (date: any): string => {
    if (date && typeof date.format === 'function') {
      return date.format('YYYY-MM-DD')
    } else if (typeof date === 'string') {
      return date.split('T')[0]
    } else if (date instanceof Date) {
      return moment(date).format('YYYY-MM-DD')
    }
    console.error('Unknown date format:', date)
    return ''
  }

  const getSelectedDates = (): string[] => {
    if (selectedDays && selectedDays.length > 0) {
      return selectedDays
        .map((d) => formatDateToString(d.activityDayLocalTimeDate))
        .filter((d) => d !== '')
    }
    return [formatDateToString(day.activityDayLocalTimeDate)]
  }

  const { formatDate } = useFormatDate()
  const formatDatesForDisplay = (): string => {
    const dateStrings = getSelectedDates()

    if (dateStrings.length === 1) {
      return formatDate(dateStrings[0])
    }

    const sortedDates = [...dateStrings].sort()

    const ranges: string[][] = []
    let currentRange: string[] = [sortedDates[0]]

    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = moment(sortedDates[i - 1])
      const currDate = moment(sortedDates[i])
      const diffInDays = currDate.diff(prevDate, 'days')

      if (diffInDays === 1) {
        currentRange.push(sortedDates[i])
      } else {
        ranges.push(currentRange)
        currentRange = [sortedDates[i]]
      }
    }
    ranges.push(currentRange)

    const formattedRanges = ranges.map((range) => {
      if (range.length === 1) {
        return formatDate(range[0])
      } else {
        return `${formatDate(range[0])} - ${formatDate(range[range.length - 1])}`
      }
    })

    if (formattedRanges.length > 5) {
      return `${sortedDates.length} dni (${formatDate(sortedDates[0])} - ${formatDate(sortedDates[sortedDates.length - 1])})`
    }

    return formattedRanges.join(', ')
  }

  const getFormattedDatesList = (): string[] => {
    const dateStrings = getSelectedDates()

    if (dateStrings.length === 1) {
      return []
    }

    const sortedDates = [...dateStrings].sort()

    const ranges: string[][] = []
    let currentRange: string[] = [sortedDates[0]]

    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = moment(sortedDates[i - 1])
      const currDate = moment(sortedDates[i])
      const diffInDays = currDate.diff(prevDate, 'days')

      if (diffInDays === 1) {
        currentRange.push(sortedDates[i])
      } else {
        ranges.push(currentRange)
        currentRange = [sortedDates[i]]
      }
    }
    ranges.push(currentRange)

    return ranges.map((range) => {
      if (range.length === 1) {
        return formatDate(range[0])
      } else {
        return `${formatDate(range[0])} - ${formatDate(range[range.length - 1])} (${range.length} dni)`
      }
    })
  }

  const closeMenu = () => {
    if (onClose) {
      onClose()
    }
  }

  const handleConfirmDayTypeChange = async () => {
    if (selectedDayType !== null) {
      setIsLoading(true)
      closeMenu()
      try {
        await dayEvents.handleChangeDayType(
          day,
          selectedDayType,
          companyId,
          vehicleId,
          getSelectedDates(),
          changeVehicleDayTypes,
        )
        if (refreshCalendar) {
          await refreshCalendar()
        }
      } catch (error) {
        console.error('Error changing day type:', error)
      } finally {
        setIsLoading(false)
      }
    }
    setIsConfirmModalOpen(false)
    setSelectedDayType(null)
  }

  const handleConfirmAction = async () => {
    if (confirmAction) {
      setIsLoading(true)
      closeMenu()
      try {
        await confirmAction.action()
        if (refreshCalendar) {
          await refreshCalendar()
        }
      } catch (error) {
        console.error('Error executing action:', error)
      } finally {
        setIsLoading(false)
        setConfirmAction(null)
      }
    }
  }

  const createConfirmAction = (
    title: string,
    text: string,
    action: () => Promise<void>,
  ) => {
    setConfirmAction({
      title,
      text:
        getSelectedDates().length === 1
          ? text
          : text.replace(
              /dla:.*\?/,
              `${translate('driverCalendar.for-following-days')}?`,
            ),
      action,
      datesList: getFormattedDatesList(),
    })
  }

  const renderDayTypeMenuItem = (dayType: DayType, disabled?: boolean) => {
    return (
      <MenuItem
        className={`tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
        onClick={(e) => {
          setSelectedDayType(dayType)
          setIsConfirmModalOpen(true)
          closeMenu()
        }}
      >
        <FlexView hAlignContent="left" vAlignContent="center">
          <span style={{ fontWeight: 500, width: 48 }}>{DayType[dayType]}</span>
          <span style={{ marginRight: 5 }}>
            {translate(`dayType.${DayType[dayType]}`)}
          </span>
        </FlexView>
      </MenuItem>
    )
  }

  return (
    <>
      <ControlledMenu
        anchorPoint={anchorPoint}
        state={isOpen ? 'open' : 'closed'}
        onClose={closeMenu}
        className="tw-z-[10000]"
        portal={true}
        onContextMenu={(e) => {
          e.preventDefault()
        }}
      >
        <MenuItemStyled
          icon={faMapPinSolid}
          onClick={() => {
            const date = moment(day.activityDayLocalTimeDate).format(
              'YYYY-MM-DD',
            )
            navigate(
              pathname.replace(
                /vehicleCalendar\/year\/.*/,
                `vehicleDailyActivity/date/${date}`,
              ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.goToDayChart')}
        </MenuItemStyled>
        <MenuItemStyled
          icon={faMapPinSolid}
          onClick={() => {
            const date = moment(day.activityDayLocalTimeDate).format(
              'YYYY-MM-DD',
            )
            navigate(
              pathname.replace(
                /vehicleCalendar\/year\/.*/,
                `vehicleWeeklyActivity/date/${date}`,
              ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.goToWeekChart')}
        </MenuItemStyled>
        <MenuDivider className={'tw-m-0 tw-p-0'} />
        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faUserSolid}
          onClick={() => {
            closeMenu()
            setIsAssignDriverModalOpen(true)
          }}
        >
          {translate('vehicleCalendarMenuActions.assignActivitiesToDriver')}
        </MenuItemStyled>
        <MenuDivider className={'tw-m-0 tw-p-0'} />
        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendar.remove-digital-data-title'),
              `${translate('driverCalendar.confirm-remove-digital-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveImportedVehicleActivity(
                  companyId,
                  vehicleId,
                  getSelectedDates(),
                ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.removeImportedActivities')}
        </MenuItemStyled>
        <MenuItemStyled
          disabled={!can('canEditActivities')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendar.remove-digital-data-title'),
              `${translate('driverCalendar.confirm-remove-digital-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveDigitalData(
                  companyId,
                  vehicleId,
                  getSelectedDates(),
                  'vehicle',
                ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.removeDigitalData')}
        </MenuItemStyled>

        <SubMenuStyled
          disabled={!can('canEditGpsData')}
          icon={faMapPinSolid}
          label={translate('vehicleCalendarMenuActions.assignBorderCrossing.title')}
        >
          <MenuItemStyled
            disabled={!can('canEditGpsData')}
            icon={faUserSolid}
            onClick={() => {
              closeMenu()
              setAssignToDriverModal('border')
            }}
          >
            {translate(
              'vehicleCalendarMenuActions.assignBorderCrossing.toSelectedDriver',
            )}
          </MenuItemStyled>
          <MenuItemStyled
            disabled={!can('canEditGpsData')}
            icon={faCarSolid}
            onClick={() => {
              createConfirmAction(
                translate(
                  'vehicleCalendarMenuActions.assignBorderCrossing.toDriverBasedOnAssociations',
                ),
                `${translate('driverCalendar.confirm-assign-border-crossings')} ${formatDatesForDisplay()}?`,
                () =>
                  dayEvents.handleAutoAssignBorderCrossingsToDriver(
                    companyId,
                    vehicleId,
                    getSelectedDates(),
                    'vehicle',
                  ),
              )
            }}
          >
            {translate(
              'vehicleCalendarMenuActions.assignBorderCrossing.toDriverBasedOnAssociations',
            )}
          </MenuItemStyled>
        </SubMenuStyled>

        <MenuItemStyled
          disabled={!day.hasGpsData || !can('canEditGpsData')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendar.remove-border-crossings-title'),
              `${translate('driverCalendar.confirm-remove-border-crossings')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveBorderCrossings(
                  companyId,
                  vehicleId,
                  getSelectedDates(),
                  'vehicle',
                ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.removeBorderCrossings')}
        </MenuItemStyled>

        <MenuDivider className={'tw-m-0 tw-p-0'} />

        <SubMenuStyled
          disabled={!can('canEditGpsData')}
          icon={faCarSolid}
          label={translate('vehicleCalendarMenuActions.assignTmsData.title')}
        >
          <MenuItemStyled
            disabled={!can('canEditGpsData')}
            icon={faUserSolid}
            onClick={() => {
              closeMenu()
              setAssignToDriverModal('tms')
            }}
          >
            {translate(
              'vehicleCalendarMenuActions.assignTmsData.toSelectedDriver',
            )}
          </MenuItemStyled>
          <MenuItemStyled
            disabled={!can('canEditGpsData')}
            icon={faCarSolid}
            onClick={(e) => {
              createConfirmAction(
                translate(
                  'vehicleCalendarMenuActions.assignTmsData.toDriverBasedOnAssociations',
                ),
                `${translate('driverCalendar.confirm-assign-tms-data')} ${formatDatesForDisplay()}?`,
                () =>
                  dayEvents.handleAssignTmsDataToDriverAutomatically(
                    companyId,
                    vehicleId,
                    getSelectedDates(),
                    'vehicle',
                  ),
              )
            }}
          >
            {translate(
              'vehicleCalendarMenuActions.assignTmsData.toDriverBasedOnAssociations',
            )}
          </MenuItemStyled>
        </SubMenuStyled>

        <MenuItemStyled
          disabled={!day.hasTmsData || !can('canEditGpsData')}
          icon={faTrashSolid}
          className="tw-text-red-500"
          onClick={(e) => {
            createConfirmAction(
              translate('driverCalendar.remove-tms-data-title'),
              `${translate('driverCalendar.confirm-remove-tms-data')} ${formatDatesForDisplay()}?`,
              () =>
                dayEvents.handleRemoveTmsData(
                  companyId,
                  vehicleId,
                  getSelectedDates(),
                  'vehicle',
                ),
            )
          }}
        >
          {translate('vehicleCalendarMenuActions.removeTmsData')}
        </MenuItemStyled>
      </ControlledMenu>

      <ConfirmModal
        isOpen={isConfirmModalOpen}
        onClose={() => {
          setIsConfirmModalOpen(false)
          setSelectedDayType(null)
        }}
        title={translate('driverCalendar.change-day-type-title')}
        text={`${translate('driverCalendar.confirm-change-day-type')} ${selectedDayType !== null ? translate(`dayType.${DayType[selectedDayType]}`) : ''}${getSelectedDates().length === 1 ? ` ${translate('driverCalendar.for')}: ${formatDatesForDisplay()}` : ` ${translate('driverCalendar.for-following-days')}:`}?`}
        actionText={translate('driverCalendar.change')}
        onConfirm={handleConfirmDayTypeChange}
        content={getFormattedDatesList()}
      />

      {confirmAction && (
        <ConfirmModal
          isOpen={true}
          onClose={() => setConfirmAction(null)}
          title={confirmAction.title}
          text={confirmAction.text}
          actionText={translate('common.confirm')}
          onConfirm={handleConfirmAction}
          content={confirmAction.datesList || []}
        />
      )}

      {isAssignDriverModalOpen && (
        <AssignActivitiesToDriverModal
          companyId={companyId}
          vehicleId={vehicleId}
          day={day}
          selectedDays={selectedDays}
          close={() => setIsAssignDriverModalOpen(false)}
          onSuccess={refreshCalendar}
        />
      )}

      {assignToDriverModal && (
        <AssignToSelectedDriverModal
          companyId={companyId}
          vehicleId={vehicleId}
          day={day}
          selectedDays={selectedDays}
          title={
            assignToDriverModal === 'border'
              ? `${translate('vehicleCalendarMenuActions.assignBorderCrossing.title')} ${translate('vehicleCalendarMenuActions.assignBorderCrossing.toSelectedDriver')}`
              : `${translate('vehicleCalendarMenuActions.assignTmsData.title')} ${translate('vehicleCalendarMenuActions.assignTmsData.toSelectedDriver')}`
          }
          onAssign={async ({
            selectedDriverId,
            selectedDates,
            dateFrom,
            dateTo,
          }) => {
            const isBorder = assignToDriverModal === 'border'
            const data = isBorder
              ? await assignBorderCrossingsToSelectedDriver({
                  companyId,
                  selectedVehicleId: vehicleId,
                  selectedDriverId,
                  selectedDates,
                  dateFrom,
                  dateTo,
                })
              : await assignTmsDataToSelectedDriver({
                  companyId,
                  selectedVehicleId: vehicleId,
                  selectedDriverId,
                  selectedDates,
                  dateFrom,
                  dateTo,
                })
            notifyOperationResults(
              data,
              translate,
              isBorder
                ? {
                    success: 'driverCalendar.border-crossings-assigned',
                    warning: 'driverCalendar.no-border-crossing-data',
                    error: 'driverCalendar.assign-border-crossings-error',
                  }
                : {
                    success: 'driverCalendar.tms-data-auto-assigned',
                    warning: 'driverCalendar.no-tms-data-to-assign',
                    error: 'driverCalendar.auto-assign-tms-error',
                  },
            )
            return data
          }}
          close={() => setAssignToDriverModal(null)}
          onSuccess={refreshCalendar}
        />
      )}

      {isLoading && (
        <div className="tw-fixed tw-inset-0 tw-z-[9999] tw-flex tw-items-center tw-justify-center tw-bg-black tw-bg-opacity-50">
          <div className="tw-rounded-lg tw-bg-white tw-p-8 tw-shadow-xl">
            <div className="tw-flex tw-flex-col tw-items-center tw-gap-4">
              <div className="tw-h-12 tw-w-12 tw-animate-spin tw-rounded-full tw-border-4 tw-border-gray-200 tw-border-t-primary"></div>
              <p className="tw-text-lg tw-font-medium tw-text-gray-700">
                {translate('driverCalendar.updating-calendar')}
              </p>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const SubMenuStyled = ({
  disabled,
  children,
  icon,
  label,
}: {
  children: React.ReactNode
  disabled?: boolean
  icon?: any
  label: string
}) => {
  return (
    <SubMenu
      disabled={disabled}
      label={
        <FlexView
          hAlignContent="left"
          vAlignContent="center"
          className="tw-gap-2"
        >
          {icon && <FaIcon icon={icon} className="tw-h-3 tw-w-3" />}
          <span>{label}</span>
        </FlexView>
      }
    >
      {children}
    </SubMenu>
  )
}

const MenuItemStyled = ({
  disabled,
  children,
  onClick,
  icon,
  className: extraClassName,
}: {
  children: React.ReactNode
  disabled?: boolean
  onClick?: (e: any) => void
  icon?: any
  className?: string
}) => {
  return (
    <MenuItem
      disabled={disabled}
      className={`tw-my-0 tw-px-4 tw-py-1 tw-text-sm tw-text-content-base ${disabled ? 'tw-cursor-not-allowed tw-opacity-50' : 'tw-cursor-pointer'}`}
      onClick={onClick}
    >
      <FlexView
        hAlignContent="left"
        vAlignContent="center"
        className="tw-gap-2"
      >
        {icon && (
          <FaIcon
            icon={icon}
            className={`tw-h-3 tw-w-3 ${extraClassName || ''}`}
          />
        )}
        <span className={extraClassName || ''}>{children}</span>
      </FlexView>
    </MenuItem>
  )
}
